import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { MAX_TAGS_PER_CUSTOMER } from '@tailoring-crm/shared';
import { useCustomerTags } from '../../lib/customers/use-customers.js';
import { Badge } from '@tailoring-crm/ui';

/**
 * A customer's tags: the ones they carry as removable chips, and a picker over the shop's
 * managed list for one more.
 *
 * It offers only tags the shop has made in Manage Tags — a tag is created there, with its colour,
 * not typed into one customer's form — and only the ones this customer is not already wearing.
 */
export function CustomerTagsInput({
  id,
  value,
  onChange,
  error,
  describedBy,
}: {
  id: string;
  value: readonly string[];
  onChange: (tags: string[]) => void;
  error?: ReactElement | null;
  describedBy?: string;
}): ReactElement {
  const { t } = useTranslation();
  const managed = useCustomerTags().data?.tags ?? [];

  const carried = new Set(value.map((tag) => tag.toLowerCase()));
  const offered = managed.filter((tag) => !carried.has(tag.name.toLowerCase()));
  const full = value.length >= MAX_TAGS_PER_CUSTOMER;

  const remove = (tag: string): void => onChange(value.filter((one) => one !== tag));

  return (
    <div className="flex flex-col gap-1.5">
      {/* A label only when there is a control to label; the hint below is plain text. */}
      {managed.length > 0 ? (
        <label htmlFor={id} className="text-sm font-medium">
          {t('customers.field.tags')}
        </label>
      ) : (
        <span className="text-sm font-medium">{t('customers.field.tags')}</span>
      )}
      {value.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5" aria-label={t('customers.field.tags')}>
          {value.map((tag) => (
            <li key={tag}>
              <Badge variant="secondary" className="gap-1 pr-1">
                {tag}
                <button
                  type="button"
                  onClick={() => remove(tag)}
                  aria-label={t('customers.tags.removeFromCustomer', { tag })}
                  className="rounded-sm p-0.5 hover:bg-foreground/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <X aria-hidden="true" className="size-3" />
                </button>
              </Badge>
            </li>
          ))}
        </ul>
      ) : null}
      {managed.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('customers.tags.noneYet')}</p>
      ) : (
        <select
          id={id}
          // Always back on the prompt: picking adds the tag, it does not hold a value.
          value=""
          onChange={(event) => {
            const picked = event.target.value;
            if (picked) onChange([...value, picked]);
          }}
          disabled={full || offered.length === 0}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
        >
          <option value="">
            {full
              ? t('customers.tags.full')
              : offered.length === 0
                ? t('customers.tags.allChosen')
                : t('customers.tags.pick')}
          </option>
          {offered.map((tag) => (
            <option key={tag.id} value={tag.name}>
              {tag.name}
            </option>
          ))}
        </select>
      )}
      {error}
    </div>
  );
}
